import {
  type PaginationState,
  type SortingState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useCallback, useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { MissingNotesItem } from '@eluve/api-contract';
import {
  Button,
  ColDefBuilder,
  Icon,
  Link,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ServerDataTable,
  Stack,
  Text,
  useToast,
} from '@eluve/components';

// Default the view to the last 7 days. Older completed appointments that
// never got a note are rarely actionable, and an unbounded scan over all
// history is what makes this endpoint slow in large environments. '0' is the
// explicit "All time" escape hatch (unbounded, deliberately slow).
const DEFAULT_MAX_AGE_DAYS = '7';
const ALL_TIME = '0';

const getUserDisplayName = (row: MissingNotesItem) =>
  [row.firstName, row.lastName].filter(Boolean).join(' ') ||
  row.email ||
  'Unknown';

const columns = new ColDefBuilder<MissingNotesItem>()
  .defaultSortable('tenantName', 'Organization')
  .linkSortable(
    'appointmentName',
    (row) =>
      `/admin/organizations/${row.tenantId}/appointments/${row.appointmentId}/details`,
    'Appointment',
  )
  .colDef({
    id: 'user',
    header: 'User',
    accessorFn: getUserDisplayName,
    cell: ({ row }) => {
      const name = getUserDisplayName(row.original);
      if (row.original.userId) {
        return <Link to={`/admin/users/${row.original.userId}`}>{name}</Link>;
      }
      return <Text size="xs">{name}</Text>;
    },
    enableSorting: true,
  })
  .defaultSortable('status', 'Status')
  .colDef({
    id: 'reason',
    accessorKey: 'failureReason',
    header: 'Reason',
    cell: ({ row }) => {
      const reason = row.original.failureReason;
      const failureType = row.original.failureType;
      if (!reason) {
        return (
          <Text size="xs" color="tertiary">
            No note generated
          </Text>
        );
      }
      return (
        <Stack>
          <Text size="xs">{reason}</Text>
          {failureType && (
            <Text size="xs" color="tertiary">
              {failureType}
            </Text>
          )}
        </Stack>
      );
    },
  })
  .dateSortable('createdAt', 'Created At')
  .dateSortable('startDate', 'Start Date')
  .dateSortable('completedAt', 'Completed At')
  .build();

export const MissingNotesTab: React.FC = () => {
  const api = useApiClient();
  const [data, setData] = useState<MissingNotesItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetchDurationMs, setFetchDurationMs] = useState<number | null>(null);
  const toast = useToast();
  const [maxAgeDays, setMaxAgeDays] = useState(DEFAULT_MAX_AGE_DAYS);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'completedAt', desc: true },
  ]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const startTime = performance.now();
    const sort = sorting[0];
    const result = await api.adminOnCall.getMissingNotes({
      query: {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        sortBy: sort?.id,
        sortDirection: sort?.desc ? 'desc' : 'asc',
        maxAgeDays: Number(maxAgeDays),
      },
    });
    const elapsed = Math.round(performance.now() - startTime);
    setFetchDurationMs(elapsed);
    if (result.status === 200) {
      setData(result.body.items);
      setTotalCount(result.body.totalCount);
    } else {
      toast.error('Failed to load missing notes. Please try again.');
    }
    setLoading(false);
  }, [api, maxAgeDays, pagination.pageIndex, pagination.pageSize, sorting]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: Math.ceil(totalCount / pagination.pageSize),
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
  });

  return (
    <Stack gap={2}>
      <Stack gap={2} direction="horizontal">
        <Select
          value={maxAgeDays}
          onValueChange={(value) => {
            setMaxAgeDays(value);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Date range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value={ALL_TIME}>All time (slow)</SelectItem>
          </SelectContent>
        </Select>
        <Stack
          className="ml-auto"
          fullWidth={false}
          gap={2}
          direction="horizontal"
        >
          <Text inline size="xs" color="tertiary">
            {loading
              ? 'Loading...'
              : `${totalCount} appointment${totalCount !== 1 ? 's' : ''} missing notes ${maxAgeDays === ALL_TIME ? '(all time)' : `in last ${maxAgeDays}d`}${fetchDurationMs !== null ? ` · ${fetchDurationMs >= 1000 ? `${(fetchDurationMs / 1000).toFixed(1)}s` : `${fetchDurationMs}ms`}` : ''}`}
          </Text>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <Icon name="RefreshCw" size="auto" />
            Refresh
          </Button>
        </Stack>
      </Stack>
      <Stack className="rounded-md border">
        <ServerDataTable<MissingNotesItem>
          table={table}
          isPending={loading}
          placeholder="No appointments missing notes found."
        />
      </Stack>
    </Stack>
  );
};
